import { Clock, Bell, CheckCircle } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { Button, Card } from '@design-system';
import { ContactSupportButton } from '@/components/ContactSupportButton';
import { quoteWaitlistApi } from '@client/api/waterlily';
import type { API } from '@client/api/types';

interface NoQuotesNYScreenProps {
  clientId: string;
  advisorId?: string;
  signupSource: 'shop_insurance' | 'guided_planning';
}

export function NoQuotesNYScreen({
  clientId,
  advisorId,
  signupSource,
}: NoQuotesNYScreenProps) {
  const { mutate: signup, isPending, isSuccess } = useMutation({
    mutationFn: (data: API.QuoteWaitlistSignupRequest) => quoteWaitlistApi.signup(data),
  });

  const handleNotifyMe = () => {
    signup({
      clientId,
      advisorId,
      state: 'NY',
      reason: 'ny_ltc_quotes',
      signupSource,
    });
  };

  return (
    <div className="mx-auto max-w-xl py-12 text-center">
      <Card className="flex flex-col items-center gap-5 px-10 py-8">
        <div className="bg-primary-50 flex size-14 items-center justify-center rounded-full">
          <Clock className="text-primary size-7" />
        </div>

        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-900">
            Insurance Quotes Coming Soon for New York
          </h3>
          <p className="text-base leading-relaxed text-gray-600">
            Most long-term care insurance carriers don&apos;t currently offer coverage in New York.
            We&apos;re in the process of adding a carrier that does, and we&apos;ll notify you as
            soon as quotes are available.
          </p>
        </div>

        <div className="flex flex-col items-center gap-3">
          {isSuccess ? (
            <div className="flex items-center gap-2 text-sm font-medium text-green-600">
              <CheckCircle className="size-4" />
              You&apos;re on the list — we&apos;ll be in touch!
            </div>
          ) : (
            <Button
              onClick={handleNotifyMe}
              disabled={isPending}
            >
              <Bell className="size-4" />
              {isPending ? 'Signing up...' : 'Notify Me When Available'}
            </Button>
          )}

          <ContactSupportButton
            subject="New York LTC insurance availability"
            variant="outline"
          />
        </div>
      </Card>
    </div>
  );
}
